import { serverTimestamp, Timestamp } from 'firebase/firestore';
import crisisTaskTemplates from '../data/crisisTaskTemplates.yaml';

/**
 * Crisis Task Generation Engine
 * Generates role-specific tasks based on crisis type and organizational context
 */

// Load and parse YAML templates
let taskTemplates = null;

const loadTaskTemplates = async () => {
  if (taskTemplates) return taskTemplates;
  
  try {
    // For now, we'll use a static import approach
    // In production, this could be loaded from Firestore or external source
    const response = await fetch('/data/crisisTaskTemplates.yaml');
    const yamlText = await response.text();
    
    // Simple YAML parser for our structured data
    // Note: In production, consider using a proper YAML library
    taskTemplates = parseYamlTemplates(yamlText);
    return taskTemplates;
  } catch (error) {
    console.error('Error loading crisis task templates:', error);
    // Fallback to embedded templates
    return getEmbeddedTemplates();
  }
};

// Simple YAML parser for our specific structure
const parseYamlTemplates = (yamlText) => {
  // This is a simplified parser for our specific YAML structure
  // In production, consider using js-yaml library
  try {
    // For MVP, we'll use the embedded templates
    return getEmbeddedTemplates();
  } catch (error) {
    console.error('Error parsing YAML:', error);
    return getEmbeddedTemplates();
  }
};

// Embedded templates as fallback (from our MVP YAML)
const getEmbeddedTemplates = () => ({
  crisis_templates: {
    data_breach: {
      display_name: "Data Breach",
      description: "Unauthorized access to sensitive data or systems",
      roles: {
        it_tech: {
          role_display_name: "IT/Technology Lead",
          phases: {
            immediate: {
              phase_display_name: "Immediate (0-2 hours)",
              tasks: [
                {
                  name: "Isolate affected systems",
                  description: "Disconnect compromised systems from network to prevent further damage",
                  priority: "critical",
                  suggested_due_hours: 1,
                  estimated_duration_minutes: 30,
                  no_due_date: false
                },
                {
                  name: "Assess scope of breach",
                  description: "Determine what data/systems were accessed and extent of compromise",
                  priority: "critical",
                  suggested_due_hours: 2,
                  estimated_duration_minutes: 120,
                  no_due_date: false
                }
              ]
            },
            short_term: {
              phase_display_name: "Short-term (2-24 hours)",
              tasks: [
                {
                  name: "Implement containment measures",
                  description: "Deploy security patches, update access controls, enhance monitoring",
                  priority: "recommended",
                  suggested_due_hours: 12,
                  estimated_duration_minutes: 240,
                  no_due_date: false
                }
              ]
            },
            ongoing: {
              phase_display_name: "Ongoing",
              tasks: [
                {
                  name: "Monitor for further intrusions",
                  description: "Continuous security monitoring and threat detection",
                  priority: "recommended",
                  suggested_due_hours: null,
                  estimated_duration_minutes: null,
                  no_due_date: true
                }
              ]
            }
          }
        },
        legal: {
          role_display_name: "Legal Counsel",
          phases: {
            immediate: {
              phase_display_name: "Immediate (0-2 hours)",
              tasks: [
                {
                  name: "Review notification requirements",
                  description: "Check regulatory obligations for breach notification (GDPR, state laws, etc.)",
                  priority: "critical",
                  suggested_due_hours: 1,
                  estimated_duration_minutes: 60,
                  no_due_date: false
                }
              ]
            },
            short_term: {
              phase_display_name: "Short-term (2-24 hours)",
              tasks: [
                {
                  name: "Draft regulatory notifications",
                  description: "Prepare required notifications to regulators and authorities",
                  priority: "critical",
                  suggested_due_hours: 8,
                  estimated_duration_minutes: 180,
                  no_due_date: false
                },
                {
                  name: "Review insurance coverage",
                  description: "Assess cyber insurance coverage and notification requirements",
                  priority: "recommended",
                  suggested_due_hours: 24,
                  estimated_duration_minutes: 90,
                  no_due_date: false
                }
              ]
            }
          }
        },
        comms_media: {
          role_display_name: "Communications/Media Lead",
          phases: {
            immediate: {
              phase_display_name: "Immediate (0-2 hours)",
              tasks: [
                {
                  name: "Prepare holding statements",
                  description: "Draft initial response statements for media and stakeholders",
                  priority: "critical",
                  suggested_due_hours: 2,
                  estimated_duration_minutes: 90,
                  no_due_date: false
                }
              ]
            },
            short_term: {
              phase_display_name: "Short-term (2-24 hours)",
              tasks: [
                {
                  name: "Draft customer communications",
                  description: "Prepare customer notification emails and website updates",
                  priority: "critical",
                  suggested_due_hours: 6,
                  estimated_duration_minutes: 120,
                  no_due_date: false
                },
                {
                  name: "Monitor media coverage",
                  description: "Track news coverage and social media mentions",
                  priority: "recommended",
                  suggested_due_hours: null,
                  estimated_duration_minutes: null,
                  no_due_date: true
                }
              ]
            }
          }
        }
      }
    },
    workplace_injury: {
      display_name: "Workplace Injury",
      description: "Employee injury requiring medical attention or investigation",
      roles: {
        talent_hr: {
          role_display_name: "HR/People Lead",
          phases: {
            immediate: {
              phase_display_name: "Immediate (0-1 hour)",
              tasks: [
                {
                  name: "Ensure medical assistance provided",
                  description: "Confirm injured person receives appropriate medical care",
                  priority: "critical",
                  suggested_due_hours: 0.25,
                  estimated_duration_minutes: 15,
                  no_due_date: false
                },
                {
                  name: "Secure incident scene",
                  description: "Preserve evidence and ensure area is safe",
                  priority: "critical",
                  suggested_due_hours: 0.5,
                  estimated_duration_minutes: 30,
                  no_due_date: false
                }
              ]
            },
            short_term: {
              phase_display_name: "Short-term (1-24 hours)",
              tasks: [
                {
                  name: "Complete incident report",
                  description: "Document details of injury and circumstances",
                  priority: "critical",
                  suggested_due_hours: 4,
                  estimated_duration_minutes: 60,
                  no_due_date: false
                },
                {
                  name: "Notify workers compensation insurer",
                  description: "Report injury to insurance provider",
                  priority: "critical",
                  suggested_due_hours: 24,
                  estimated_duration_minutes: 30,
                  no_due_date: false
                }
              ]
            }
          }
        },
        safety_security: {
          role_display_name: "Safety/Security Lead",
          phases: {
            immediate: {
              phase_display_name: "Immediate (0-1 hour)",
              tasks: [
                {
                  name: "Conduct initial safety assessment",
                  description: "Assess if area is safe for continued operations",
                  priority: "critical",
                  suggested_due_hours: 0.5,
                  estimated_duration_minutes: 30,
                  no_due_date: false
                }
              ]
            },
            short_term: {
              phase_display_name: "Short-term (1-24 hours)",
              tasks: [
                {
                  name: "Investigate root cause",
                  description: "Determine what led to the injury",
                  priority: "recommended",
                  suggested_due_hours: 8,
                  estimated_duration_minutes: 120,
                  no_due_date: false
                },
                {
                  name: "Review safety procedures",
                  description: "Assess if safety protocols need updating",
                  priority: "recommended",
                  suggested_due_hours: 24,
                  estimated_duration_minutes: 90,
                  no_due_date: false
                }
              ]
            }
          }
        }
      }
    },
    fire_emergency: {
      display_name: "Fire Emergency",
      description: "Fire or explosion at company facilities",
      roles: {
        safety_security: {
          role_display_name: "Safety/Security Lead",
          phases: {
            immediate: {
              phase_display_name: "Immediate (0-30 minutes)",
              tasks: [
                {
                  name: "Confirm emergency services contacted",
                  description: "Ensure fire department and emergency services are notified",
                  priority: "critical",
                  suggested_due_hours: 0.1,
                  estimated_duration_minutes: 5,
                  no_due_date: false
                },
                {
                  name: "Execute evacuation procedures",
                  description: "Ensure all personnel evacuate safely",
                  priority: "critical",
                  suggested_due_hours: 0.25,
                  estimated_duration_minutes: 15,
                  no_due_date: false
                },
                {
                  name: "Account for all personnel",
                  description: "Conduct headcount at assembly points",
                  priority: "critical",
                  suggested_due_hours: 0.5,
                  estimated_duration_minutes: 30,
                  no_due_date: false
                }
              ]
            },
            short_term: {
              phase_display_name: "Short-term (30 minutes - 4 hours)",
              tasks: [
                {
                  name: "Coordinate with emergency services",
                  description: "Liaise with fire department and provide facility information",
                  priority: "critical",
                  suggested_due_hours: 1,
                  estimated_duration_minutes: 60,
                  no_due_date: false
                },
                {
                  name: "Assess facility damage",
                  description: "Initial assessment of damage and safety for re-entry",
                  priority: "recommended",
                  suggested_due_hours: 4,
                  estimated_duration_minutes: 90,
                  no_due_date: false
                }
              ]
            }
          }
        },
        facilities: {
          role_display_name: "Facilities Lead",
          phases: {
            immediate: {
              phase_display_name: "Immediate (0-30 minutes)",
              tasks: [
                {
                  name: "Shut off utilities if safe",
                  description: "Turn off gas, electricity if it can be done safely",
                  priority: "critical",
                  suggested_due_hours: 0.25,
                  estimated_duration_minutes: 10,
                  no_due_date: false
                }
              ]
            },
            short_term: {
              phase_display_name: "Short-term (30 minutes - 4 hours)",
              tasks: [
                {
                  name: "Contact insurance company",
                  description: "Notify property insurance of fire damage",
                  priority: "critical",
                  suggested_due_hours: 2,
                  estimated_duration_minutes: 30,
                  no_due_date: false
                },
                {
                  name: "Arrange temporary facilities",
                  description: "Secure alternative workspace if needed",
                  priority: "recommended",
                  suggested_due_hours: 4,
                  estimated_duration_minutes: 120,
                  no_due_date: false
                }
              ]
            }
          }
        },
        comms_media: {
          role_display_name: "Communications/Media Lead",
          phases: {
            short_term: {
              phase_display_name: "Short-term (30 minutes - 4 hours)",
              tasks: [
                {
                  name: "Prepare media statement",
                  description: "Draft statement about fire incident for media inquiries",
                  priority: "recommended",
                  suggested_due_hours: 2,
                  estimated_duration_minutes: 60,
                  no_due_date: false
                },
                {
                  name: "Notify key stakeholders",
                  description: "Inform customers, suppliers of potential service disruption",
                  priority: "recommended",
                  suggested_due_hours: 4,
                  estimated_duration_minutes: 45,
                  no_due_date: false
                }
              ]
            }
          }
        }
      }
    }
  }
});

const CRISIS_TYPE_MAPPING = {
  'Fire': 'fire',
  'Flood': 'flood',
  'Kidnap for Ransom': 'kidnap_for_ransom',
  'Medical Emergency': 'medical_emergency',
  'Active Threat': 'active_threat',
  'Aggressive Customer': 'aggressive_customer',
  'Earthquake': 'earthquake',
  'Exercise': 'exercise',
  'Extortion': 'extortion',
  'Meeting/Event Incident': 'meeting_event_incident',
  'Protest/Civil Unrest': 'protest_civil_unrest',
  'Reputational Incident': 'reputational_incident',
  'Suspicious Item/Bomb Threat': 'suspicious_item_bomb_threat',
  'Threats Against Executive': 'threats_against_executive',
  'Critical Incident': 'critical_incident',
  'Cyclone/Severe Weather': 'cyclone_severe_weather',
  'Cyber Incident': 'cyber_incident',
  'Data Breach': 'data_breach',
  'Workplace Injury': 'workplace_injury',
  'Other': 'other'
};

// Convert crisis type display name to template key
const getCrisisTypeKey = (crisisType) => {
  if (!crisisType) return null;
  
  // Handle "Other" crisis type
  if (crisisType === 'Other') return null;
  
  // Convert display name to snake_case key
  const keyMap = CRISIS_TYPE_MAPPING;
  
  return keyMap[crisisType] || null;
};

// Get organization's CMT members with their roles
const getOrgCmtMembers = async (db, orgId) => {
  try {
    // This would fetch from your CMT members collection
    // For now, return empty array - will be implemented when CMT data structure is confirmed
    return [];
  } catch (error) {
    console.error('Error fetching CMT members:', error);
    return [];
  }
};

// Calculate due date based on suggested hours and incident occurrence time
const calculateDueDate = (suggestedHours, incidentOccurredAt) => {
  if (!suggestedHours) return null;
  
  const baseTime = incidentOccurredAt ? 
    incidentOccurredAt.toDate() : 
    new Date();
  
  return new Date(baseTime.getTime() + (suggestedHours * 60 * 60 * 1000));
};

// Auto-generate phase display names based on phase key
const getPhaseDisplayName = (phaseKey, crisisType) => {
  const phaseNames = {
    immediate: "Immediate (0-2 hours)",
    short_term: "Short-term (2-24 hours)", 
    ongoing: "Ongoing"
  };
  
  // Customize based on crisis type if needed
  if (crisisType === 'fire' || crisisType === 'fire_emergency') {
    return {
      immediate: "Immediate (0-30 minutes)",
      short_term: "Short-term (30 minutes - 4 hours)",
      ongoing: "Ongoing"
    }[phaseKey] || phaseNames[phaseKey];
  }
  
  return phaseNames[phaseKey] || phaseKey.charAt(0).toUpperCase() + phaseKey.slice(1);
};

// Normalize role data to handle both old and new formats
const normalizeRoleData = (roleData, crisisKey) => {
  // If it already has the phases wrapper (old format), return as-is
  if (roleData.phases) {
    return roleData;
  }
  
  // New format: convert direct phase keys to phases wrapper
  const normalizedRoleData = {
    role_display_name: roleData.role_display_name,
    phases: {}
  };
  
  // Convert each phase from array format to object format
  Object.entries(roleData).forEach(([key, value]) => {
    if (key !== 'role_display_name' && Array.isArray(value)) {
      normalizedRoleData.phases[key] = {
        phase_display_name: getPhaseDisplayName(key, crisisKey),
        tasks: value
      };
    }
  });
  
  return normalizedRoleData;
};

// Generate tasks for a specific crisis type
export const generateCrisisTasks = async (crisisType, incidentData, orgId, db) => {
  try {
    console.log('Generating crisis tasks for:', crisisType);
    
    const templates = await loadTaskTemplates();
    const crisisKey = getCrisisTypeKey(crisisType);
    
    if (!crisisKey || !templates.crisis_templates[crisisKey]) {
      console.log('No template found for crisis type:', crisisType);
      return [];
    }
    
    const crisisTemplate = templates.crisis_templates[crisisKey];
    const cmtMembers = await getOrgCmtMembers(db, orgId);
    const generatedTasks = [];
    
    // Generate tasks for each role
    Object.entries(crisisTemplate.roles).forEach(([roleKey, roleData]) => {
      // Normalize role data to handle both formats
      const normalizedRoleData = normalizeRoleData(roleData, crisisKey);
      
      Object.entries(normalizedRoleData.phases).forEach(([phaseKey, phaseData]) => {
        phaseData.tasks.forEach((taskTemplate) => {
          const dueDate = taskTemplate.no_due_date ? 
            null : 
            calculateDueDate(taskTemplate.suggested_due_hours, incidentData.occurredAt);
          
          const task = {
            id: `generated_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: taskTemplate.name,
            title: taskTemplate.name,
            description: taskTemplate.description,
            assignedRole: roleKey,
            roleDisplayName: normalizedRoleData.role_display_name,
            phase: phaseKey,
            phaseDisplayName: phaseData.phase_display_name,
            priority: taskTemplate.priority,
            suggestedDueDate: dueDate,
            suggestedDueHours: taskTemplate.suggested_due_hours,
            estimatedDurationMinutes: taskTemplate.estimated_duration_minutes,
            noDueDate: taskTemplate.no_due_date,
            isAutoGenerated: true,
            crisisType: crisisType,
            crisisTypeKey: crisisKey,
            source: 'crisis_template'
          };
          
          generatedTasks.push(task);
        });
      });
    });
    
    console.log(`Generated ${generatedTasks.length} tasks for ${crisisType}`);
    return generatedTasks;
    
  } catch (error) {
    console.error('Error generating crisis tasks:', error);
    return [];
  }
};

// Group tasks by priority and phase for display
export const groupTasksForDisplay = (tasks) => {
  const grouped = {
    critical: { immediate: [], short_term: [], ongoing: [] },
    recommended: { immediate: [], short_term: [], ongoing: [] },
    optional: { immediate: [], short_term: [], ongoing: [] }
  };
  
  tasks.forEach(task => {
    const priority = task.priority || 'optional';
    const phase = task.phase || 'short_term';
    
    if (grouped[priority] && grouped[priority][phase]) {
      grouped[priority][phase].push(task);
    }
  });
  
  return grouped;
};

// Convert generated task to Firestore task format
export const convertToFirestoreTask = (generatedTask, incidentId, orgId, createdBy) => {
  return {
    name: generatedTask.name,
    title: generatedTask.title,
    description: generatedTask.description,
    incidentId: incidentId,
    orgId: orgId,
    assignedRole: generatedTask.assignedRole,
    status: 'not_started',
    dueDate: generatedTask.suggestedDueDate,
    createdAt: serverTimestamp(),
    createdBy: createdBy,
    type: 'crisis_response',
    priority: generatedTask.priority,
    phase: generatedTask.phase,
    isAutoGenerated: true,
    source: 'crisis_template',
    estimatedDurationMinutes: generatedTask.estimatedDurationMinutes
  };
};

export default {
  generateCrisisTasks,
  groupTasksForDisplay,
  convertToFirestoreTask,
  loadTaskTemplates
}; 